/**
 * The option and variant matrix editor's draft (MERCH-87, ADR 0009 §3), pure. The server's
 * ListingVariant.matrix answer → a draft of options and rows; the draft → ListingVariant.save's
 * request, carrying only what changed. The server checks everything again (lib/catalog/variantWrites):
 * limits, unique values, known combinations, unique SKU/barcode, the image being one of the
 * listing's approved photos, and stock rows.
 *
 *   options: [{ id, name, values: 'S, M, L', style }]            (values typed comma-separated)
 *   rows:    [{ key, id, default, option_values, sku, barcode, price, mrp, media_id, active,
 *               qty, count, stock, isNew }]                         (prices in rupees)
 */
import { paiseToRupees, rupeesToPaise } from './money';
import { isLotLine } from './orders';

export const DEFAULT_LIMITS = Object.freeze({ options: 3, values: 20, variants: 100 });
export const STYLES = Object.freeze([{ value: 'button', label: 'Buttons' }, { value: 'swatch', label: 'Colour swatches' }]);
const SWATCH_NAMES = /^(colou?r|shade)$/i;
const CODE = /^[A-Za-z0-9][A-Za-z0-9._\-/]*$/;

const splitValues = (text) => String(text || '').split(',').map((v) => v.trim()).filter(Boolean);
const lower = (v) => String(v).trim().toLowerCase();
let seq = 0;
const nextKey = (p) => `${p}-${(seq += 1)}`;

/** The saved options → the draft. */
export const optionsDraft = (options) => (options || []).map((o) => ({ id: nextKey('opt'), name: o.name, values: (o.values || []).join(', '), style: o.style || 'button', swatches: o.swatches || null }));

/** A new blank option (a colour-like name later picks swatches by default on the server). */
export const blankOption = () => ({ id: nextKey('opt'), name: '', values: '', style: 'button', swatches: null });

/** The draft → the options sent: names and values trimmed, empty ones dropped. */
export function optionsOf(draft) {
  return (draft || []).filter((o) => String(o.name || '').trim()).map((o) => {
    const name = String(o.name).trim();
    const values = splitValues(o.values);
    const style = o.style || (SWATCH_NAMES.test(name) ? 'swatch' : 'button');
    const swatches = o.swatches ? Object.fromEntries(Object.entries(o.swatches).filter(([k]) => values.includes(k))) : null;
    return { name, values, style, ...(swatches && Object.keys(swatches).length ? { swatches } : {}) };
  });
}

/** { 'options' | 'options.<i>.name' | 'options.<i>.values': message } for the options draft. */
export function optionErrors(draft, limits = DEFAULT_LIMITS) {
  const e = {};
  const named = (draft || []).filter((o) => String(o.name || '').trim() || String(o.values || '').trim());
  if (named.length > limits.options) e.options = `At most ${limits.options} options`;
  const seen = new Set();
  (draft || []).forEach((o, i) => {
    const name = String(o.name || '').trim();
    const values = splitValues(o.values);
    if (!name && !values.length) return;
    if (!name) e[`options.${i}.name`] = 'Name the option';
    else if (seen.has(lower(name))) e[`options.${i}.name`] = 'Another option has this name';
    seen.add(lower(name));
    if (!values.length) e[`options.${i}.values`] = 'Add at least one value';
    else if (values.length > limits.values) e[`options.${i}.values`] = `At most ${limits.values} values`;
    else if (new Set(values.map(lower)).size !== values.length) e[`options.${i}.values`] = 'Each value appears once';
  });
  return e;
}

/** Every combination of the options' values, in order: [{ Size: 'S', Colour: 'Blue' }, …]. */
export function combinations(options) {
  return (options || []).reduce((acc, o) => acc.flatMap((c) => o.values.map((v) => ({ ...c, [o.name]: v }))), [{}]);
}

/** A combination's identity, case-insensitive, in the options' order. */
export const comboKey = (values, options) => (options || []).map((o) => lower((values || {})[o.name] ?? '')).join('|');
/** "M / Blue" (the options' order), or "Default" for the one variant of a listing without options. */
export const labelOf = (values, options) => (options || []).map((o) => (values || {})[o.name]).filter(Boolean).join(' / ') || 'Default';

/** The server's matrix → the draft rows (prices in rupees, the counted stock for existing lots). */
export function rowsDraft(matrix) {
  return ((matrix && matrix.variants) || []).map((v) => ({
    key: v.id != null ? `v-${v.id}` : 'default',
    id: v.id,
    unit_id: v.unit_id,
    default: Boolean(v.default),
    option_values: { ...(v.option_values || {}) },
    sku: v.sku || '',
    barcode: v.barcode || '',
    price: paiseToRupees(v.price_paise),
    mrp: paiseToRupees(v.mrp_paise),
    media_id: v.media_id != null ? String(v.media_id) : '',
    active: v.active !== false,
    count: isLotLine(v.stock) ? String(v.stock.qty_on_hand) : '',
    stock: v.stock || null,
    readiness: v.readiness || null,
    isNew: false,
  }));
}

/**
 * Add a row for each combination of `options` no row has yet (opening stock 0, inactive values
 * inherited from nothing). Rows whose combination is no longer possible are kept and reported
 * in `stale` (the store removes them, or the server refuses the save).
 */
export function generate(rows, options) {
  const have = new Set((rows || []).map((r) => comboKey(r.option_values, options)));
  const added = combinations(options).filter((c) => !have.has(comboKey(c, options))).map((c) => ({
    key: nextKey('new'), id: null, unit_id: null, default: false, option_values: c, sku: '', barcode: '', price: '', mrp: '', media_id: '', active: true, qty: '0', count: '', stock: null, readiness: null, isNew: true,
  }));
  return { rows: [...(rows || []), ...added], added: added.length };
}

/** Rows whose values do not fit the options (a value removed, an option added). */
export function staleRows(rows, options) {
  const combos = new Set(combinations(options).map((c) => comboKey(c, options)));
  return (rows || []).filter((r) => !combos.has(comboKey(r.option_values, options)));
}

/** Bulk edits: a price (rupees) for every row; stock for every quantity row (new: opening, existing: the count). */
export function applyBulk(rows, { price, qty } = {}) {
  return (rows || []).map((r) => {
    const next = { ...r };
    if (price != null && price !== '') next.price = String(price);
    if (qty != null && qty !== '') {
      if (r.isNew) next.qty = String(qty);
      else if (isLotLine(r.stock)) next.count = String(qty);
    }
    return next;
  });
}

/** { 'variants' | 'variants.<key>.<field>': message } for the rows. */
export function rowErrors(rows, options, limits = DEFAULT_LIMITS) {
  const e = {};
  if ((rows || []).length > limits.variants) e.variants = `At most ${limits.variants} variants`;
  const combos = new Map();
  const codes = { sku: new Map(), barcode: new Map() };
  for (const r of rows || []) {
    const at = `variants.${r.key}`;
    const combo = comboKey(r.option_values, options);
    if (options.length && combos.has(combo)) e[`${at}.option_values`] = 'Another variant has these values';
    combos.set(combo, r.key);
    for (const field of ['sku', 'barcode']) {
      const v = String(r[field] || '').trim();
      if (!v) continue;
      if (v.length > 64 || !CODE.test(v)) e[`${at}.${field}`] = 'Letters, digits and . _ - / only';
      else if (codes[field].has(lower(v))) e[`${at}.${field}`] = `Another variant has this ${field === 'sku' ? 'SKU' : 'barcode'}`;
      codes[field].set(lower(v), r.key);
    }
    for (const field of ['price', 'mrp']) if (String(r[field] || '').trim() && rupeesToPaise(r[field]) == null) e[`${at}.${field}`] = 'Enter an amount in rupees';
    if (r.isNew && !/^\d+$/.test(String(r.qty ?? '0'))) e[`${at}.qty`] = 'A whole number';
    if (!r.isNew && r.count !== '' && r.count != null && !/^\d+$/.test(String(r.count))) e[`${at}.count`] = 'A whole number';
  }
  return e;
}

const money = (v) => (String(v ?? '').trim() === '' ? null : rupeesToPaise(v));
const code = (v) => String(v || '').trim();
const media = (v) => (v === '' || v == null ? null : Number(v));

/** One row → the fields ListingVariant.save takes for it. */
function fieldsOf(r, options) {
  return {
    ...(options.length ? { option_values: Object.fromEntries(options.map((o) => [o.name, r.option_values[o.name]])) } : {}),
    sku: code(r.sku),
    barcode: code(r.barcode),
    price_paise: money(r.price),
    mrp_paise: money(r.mrp),
    media_id: media(r.media_id),
    active: Boolean(r.active),
  };
}

/**
 * The draft → { request, keys }: ListingVariant.save's request (options when they changed; new
 * rows in full; existing rows with just their changed fields, `{ id }`, or `{ default: true }`
 * before the default row exists; `remove` for rows deleted from the draft) and the draft row key
 * of each `request.variants` entry, in order (the server's `variants.<i>` errors name them).
 * Stock counts of existing lots are not here: they go through the audited stock count
 * (InventoryUnit update), see `countsOf`.
 */
export function savePayload({ listingId, options, savedOptions, rows, savedRows }) {
  const out = { listing_id: listingId };
  const keys = [];
  const opts = optionsOf(options);
  const before = optionsOf(optionsDraft(savedOptions));
  if (JSON.stringify(opts) !== JSON.stringify(before)) out.options = opts;
  const variants = [];
  const savedBy = new Map((savedRows || []).map((r) => [r.key, r]));
  for (const r of rows || []) {
    const now = fieldsOf(r, opts);
    if (r.isNew) {
      variants.push({ ...now, qty_on_hand: Number(r.qty || 0) });
      keys.push(r.key);
      continue;
    }
    const was = savedBy.get(r.key);
    const prev = was ? fieldsOf(was, opts) : {};
    const changed = Object.fromEntries(Object.entries(now).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(prev[k])));
    if (!Object.keys(changed).length) continue;
    variants.push({ ...(r.id != null ? { id: r.id } : { default: true }), ...changed });
    keys.push(r.key);
  }
  if (variants.length) out.variants = variants;
  const kept = new Set((rows || []).map((r) => r.key));
  const remove = (savedRows || []).filter((r) => r.id != null && !kept.has(r.key)).map((r) => r.id);
  if (remove.length) out.remove = remove;
  return { request: out, keys };
}

/** True when the request saves something beyond its listing id. */
export const hasChanges = (request) => Boolean(request && (request.options || request.variants || request.remove));

/** Existing quantity lots whose count changed → [{ unit_id, qty_on_hand, label }] for the audited stock count. */
export function countsOf(rows, savedRows, options) {
  const savedBy = new Map((savedRows || []).map((r) => [r.key, r]));
  return (rows || []).filter((r) => !r.isNew && isLotLine(r.stock) && r.count !== '' && savedBy.has(r.key) && String(r.count) !== String(savedBy.get(r.key).count))
    .map((r) => ({ unit_id: r.unit_id, qty_on_hand: Number(r.count), label: labelOf(r.option_values, optionsOf(options)), key: r.key }));
}

/** A 422's details → the editor's error keys (`keys`: savePayload's, so variants.<i> → that row's key). */
export function serverErrors(ex, keys) {
  const details = (ex && ex.response && ex.response.data && ex.response.data.details) || (ex && ex.details) || [];
  const out = {};
  for (const d of Array.isArray(details) ? details : []) {
    const m = /^variants\.(\d+)\.(\w+)/.exec(d.path || '');
    if (m && keys && keys[Number(m[1])] != null) out[`variants.${keys[Number(m[1])]}.${m[2]}`] = d.message;
    else if (d.path) out[d.path] = d.message;
  }
  return out;
}
